/**
 * The confirmation dialogue — **the only caller of a push** (`09-tms-write-paths.md` §5.1).
 *
 * Gate 4: *"Every write requires a deliberate interface action **plus a confirmation
 * dialogue naming exactly what will be written and where**."* One component serves both
 * paths, so W2 marks and W2/W3 report text cannot acquire different standards of disclosure.
 *
 * It states, in this order and always:
 *
 *  1. **What** — the exact payload, itemised: every criterion and its mark with the computed
 *     outcome, or the report text and its character count;
 *  2. **Where** — the TMS surface, named;
 *  3. **What state it lands in** — *"written to TMS as draft; publishing stays a deliberate
 *     step you take inside TMS"*, on every push, in the spec's own words;
 *  4. **Any blocker** — when one exists the confirm button is not drawn at all, and the
 *     reason stands in its place;
 *  5. **Any gap between the payload and the authoring surface** — neither push route carries
 *     a body, so both send what the server has STORED. Where the screen behind this dialogue
 *     holds something else, `unsavedEdits` says so in as many words. Item 1 is only honest if
 *     the thing itemised is the thing that would be sent.
 *
 * **It is rendered in the page flow, not as a floating overlay.** That is this build's house
 * pattern — `.overridebox` on the appointments page says so in as many words: *"The mockup
 * draws it as a modal dialog; this build states it in the page flow instead, so the decision
 * cannot be dismissed by a stray click and the conflict it names stays on screen beside the
 * slot it concerns."* The same reasoning applies with more force to a push. It carries
 * `role="dialog"` and `aria-modal="false"` so it is announced as the decision it is without
 * claiming to trap focus it does not trap.
 */

import { formatPayloadForTms, tidyCriterionDisplayName, type TmsMarksPayload, type TmsPushPayload, type TmsReportPayload } from "@hockeytd/shared";

/** Where each payload lands, named on the dialogue (§5.1 item 2). */
export const TMS_SURFACE: Readonly<Record<TmsPushPayload["kind"], string>> = {
  marks: "the TMS marking section",
  report: "the TMS marking section's report text",
};

/** §B4 and §B6's shared landing-state sentence, verbatim. */
export const DRAFT_ONLY_NOTE =
  "Pushed as draft. Publishing remains a deliberate step you take inside TMS — nothing here " +
  "makes the grading final.";

/**
 * Said when the authoring surface holds text the server has not been given. The push routes
 * take no body: what goes to TMS is what the server has stored, and the payload above is that
 * text. This names the gap rather than papering over it with a silent save.
 */
export const UNSAVED_EDITS_NOTE =
  "Your unsaved edits will not be pushed — save first to include them. What is shown above " +
  "is the report as saved, and it is what would be sent.";

export interface PushConfirmProps {
  /** Exactly what would be sent. The dialogue itemises this and nothing else. */
  payload: TmsPushPayload;
  /**
   * A SECOND payload the same press would send (um-app/06 §4.5's single press: an umpire's
   * marks and their final report go together).
   *
   * Optional, and absent for every single-part push, so no existing call site changes. It is
   * a second payload rather than a second dialogue because gate 4 requires a confirmation
   * *"naming exactly what will be written"* — for one press that is both payloads, and two
   * dialogues would be two presses again. The summaries are the SAME components the single
   * payload uses, so the two halves cannot acquire different standards of disclosure.
   */
  alsoSending?: TmsPushPayload | null;
  officialName: string;
  /** `Push As Draft` (§B4) or `Confirm Push` / `Confirm Repush` (§B6). */
  confirmLabel: string;
  /** A reason the push cannot proceed. Present ⇒ no confirm button is drawn (§5.1 item 4). */
  blocker?: string | null;
  /**
   * The authoring surface holds edits the server has not been given. The payload above is
   * still exactly what would be sent — the push routes carry no body and send what is stored
   * — so this is a **warning, not a blocker**: the press stays available, and the person is
   * told plainly which of the two texts is going. Nothing is chained; saving stays a
   * separate, deliberate act.
   */
  unsavedEdits?: boolean;
  /**
   * RUN 96 - one more sentence the press owes the reader: for the report push through the TMS
   * Push extension, that the marks go with the report and that the extension's own window will
   * show everything again, field by field, before anything is saved.
   */
  note?: string | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function MarksSummary({ payload }: { payload: TmsMarksPayload }): React.JSX.Element {
  return (
    <>
      <p>
        All <b>{payload.marks.length}</b> category marks and the computed Performance Outcome
        of <b data-testid="confirm-outcome">{payload.outcome ?? "—"}</b> will be pushed to{" "}
        {TMS_SURFACE.marks} for <b>{payload.subjectName}</b>.
      </p>
      <table className="pushconfirm__table">
        <tbody>
          {payload.marks.map((mark) => (
            <tr key={mark.criterion_stable_key} data-testid={`confirm-${mark.criterion_stable_key}`}>
              <th scope="row">{tidyCriterionDisplayName(mark.name)}</th>
              <td className="num">{mark.weight}</td>
              <td className="num">{mark.na ? "N/A" : (mark.value ?? "—")}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pushconfirm__weights">
        Weights {payload.liveWeightTotal} of {payload.weightTotal}. A criterion marked{" "}
        <b>N/A</b> is left out of the total; the zero-weight row is sent with the rest and
        moves nothing.
      </p>
    </>
  );
}

function ReportSummary({ payload }: { payload: TmsReportPayload }): React.JSX.Element {
  return (
    <>
      <p>
        Your report text (<b data-testid="confirm-charcount">{payload.charCount}</b>{" "}
        characters) will be written to {TMS_SURFACE.report} for <b>{payload.subjectName}</b>.
      </p>
      <pre className="previewbox" data-testid="confirm-text">
        {payload.text}
      </pre>
    </>
  );
}

export function PushConfirm({
  payload,
  alsoSending = null,
  officialName,
  confirmLabel,
  blocker = null,
  unsavedEdits = false,
  note = null,
  busy = false,
  onConfirm,
  onCancel,
}: PushConfirmProps): React.JSX.Element {
  return (
    <div
      className="pushconfirm"
      role="dialog"
      aria-modal="false"
      aria-labelledby="pushConfirmTitle"
      data-testid="push-confirm"
    >
      <b id="pushConfirmTitle">
        {alsoSending !== null
          ? "Push Marks And Report To TMS"
          : payload.kind === "marks"
            ? "Push Marks To TMS"
            : "Push To TMS"}{" "}
        — {officialName}
      </b>

      {payload.kind === "marks" ? (
        <MarksSummary payload={payload} />
      ) : (
        <ReportSummary payload={payload} />
      )}

      {/* The second half of a single press, itemised to the same standard as the first, and
          announced as one press so nobody reads it as two. */}
      {alsoSending === null ? null : (
        <div className="pushconfirm__second" data-testid="push-confirm-second">
          <p className="pushconfirm__and">
            <b>And, in the same press —</b> one press sends both. Each half is recorded on its
            own receipt, so if one lands and the other does not, you will be told which.
          </p>
          {alsoSending.kind === "marks" ? (
            <MarksSummary payload={alsoSending} />
          ) : (
            <ReportSummary payload={alsoSending} />
          )}
        </div>
      )}

      {note === null ? null : (
        <p className="pushconfirm__note" data-testid="push-confirm-note">
          {note}
        </p>
      )}

      {unsavedEdits ? (
        <p className="pushconfirm__unsaved" role="alert" data-testid="push-unsaved-edits">
          <b>Unsaved Edits —</b> {UNSAVED_EDITS_NOTE}
        </p>
      ) : null}

      <p className="pushconfirm__draft" data-testid="push-draft-note">
        {DRAFT_ONLY_NOTE}
      </p>

      {blocker === null ? null : (
        <p className="pushconfirm__blocker" role="alert" data-testid="push-blocker">
          <b>Cannot Push —</b> {blocker}
        </p>
      )}

      <div className="pushconfirm__acts">
        {blocker === null ? (
          <button
            type="button"
            className="gbtn gbtn--solid"
            data-testid="push-confirm-ok"
            disabled={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        ) : null}
        <button type="button" className="gbtn" data-testid="push-confirm-cancel" onClick={onCancel}>
          Cancel
        </button>
        <span className="btnhint">
          Nothing is sent until you press {blocker === null ? confirmLabel : "the confirm button"}.
        </span>
      </div>
    </div>
  );
}

/**
 * The clipboard rendering the dialogue's own preview matches — exported so a test can assert
 * that what was shown and what would be copied are the same text.
 */
export function confirmPreviewText(payload: TmsPushPayload): string {
  return formatPayloadForTms(payload);
}
